import axios from "axios";

// functions
import { getErrorMessage } from "../functions/getErrorMessage";

const POLLING_INTERVAL_DURATION = 1000; // 1 second - used for signature polling

let activeBillectaClient = null;

const initiateGetBankAccounts = async (
  { socialsecuritynumber, otherDevice },
  stateRef,
  dispatch
) => {
  if (!socialsecuritynumber) {
    return;
  }

  const appdata = stateRef.current;
  const { ajax_url, nonce } = appdata?.backendSetup;

  dispatch({
    type: "setBillectaStatus",
    value: "requesting-bankaccounts",
  });

  let get_bank_account_data = new FormData();
  get_bank_account_data.set(
    "action",
    "ktk_donations_initiate_get_bank_account"
  );
  get_bank_account_data.set("nonce", nonce);
  get_bank_account_data.set("socialsecuritynumber", socialsecuritynumber);
  get_bank_account_data.set("otherDevice", otherDevice);

  try {
    const res = await axios.post(ajax_url, get_bank_account_data);

    if (!res.data?.SessionToken || !res.data?.SessionId) {
      throw new Error("Missing SessionToken or SessionId from API");
    }

    const client = new window.Billecta(res.data.SessionToken, res.data.SessionId);
    activeBillectaClient = client;

    const viewportWidth = window.innerWidth;
    const clientWidth = viewportWidth < 400 ? viewportWidth - 60 : 400;

     client.start({
      selector: "#billecta-bank-account-selector",
      width: `${clientWidth}px`,
      onSuccessful: (data) => {
        
        const appdata = stateRef.current;
        const banks = appdata?.initialServerData?.billecta?.banks;
        
        const accounts = [{
          ClearingNumber: data.clearingNo,
          AccountNumber: data.accountNo,
          BankName: data.bankName,
        }];
        
        dispatch({ type: "setBillectaBankAccounts", value: accounts });
        
        const clearingValue = data.clearingNo.substring(0, 4);
        const accountValue = data.accountNo;
        
        // Find the bank ID from the bank name
        let bankValue = data.bankName || "";
        if (banks && data.bankName) {
          const matchedBank = banks.find(bank => 
            bank.name.toLowerCase() === data.bankName.toLowerCase()
          );
          if (matchedBank) {
            bankValue = matchedBank.id;
          }
        }
        
        dispatch({
          type: "setFormValue",
          value: { name: "clearing", value: clearingValue },
        });
        dispatch({
          type: "setFormValue",
          value: { name: "account", value: accountValue },
        });
        dispatch({
          type: "setFormValue",
          value: { name: "bank", value: bankValue },
        });
        
        dispatch({ type: "setBillectaStatus", value: "success-bankaccounts" });
        activeBillectaClient = null;
      },
      onAborted: (data) => {
        activeBillectaClient = null;
        handleAbortStatus(dispatch);
        
        if (data.errorMessage) {
          dispatch({
            type: "setBillectaErrorMessage",
            value: data.errorMessage,
          });
        }
      },
      onFailed: (data) => {
        dispatch({ type: "setBillectaStatus", value: "failed-bankaccounts" });
        activeBillectaClient = null;
        
        const errorMessage = data.errorMessage || 
          "Kunde inte hämta bankkonto. Fyll i uppgifter manuellt istället.";
        
        dispatch({
          type: "setBillectaErrorMessage",
          value: errorMessage,
        });
      }
    });
  } catch (error) {
    dispatch({ type: "setBillectaStatus", value: "failed-bankaccounts" });
    dispatch({
      type: "setBillectaErrorMessage",
      value: "Kunde inte initiera bankkontohämtning. Var god försök igen.",
    });
  }
};

const initiateSignBankId = async (eventTarget, stateRef, dispatch) => {
  dispatch({
    type: "setBillectaStatus",
    value: "requesting-signage",
  });

  dispatch({
    type: "setSubmitIsLoading",
    value: true,
  });

  const appdata = stateRef.current;
  const { formData, initialServerData } = appdata;
  const { ajax_url, nonce, post_id } = appdata?.backendSetup;

  let form_data = new FormData(eventTarget);
  form_data.set("action", "ktk_donations_initiate_bankid_signature");
  form_data.set("nonce", nonce);
  form_data.set("donation_type", formData.donation_type);
  form_data.set("campaign_id", initialServerData.campaign_id);
  form_data.set("cancelUrl", initialServerData.current_url);
  form_data.set("returnUrl", initialServerData.thank_you_url);
  form_data.set("form_id", post_id);

  for (var key in formData) {
    form_data.set(key, formData[key]);
  }

  try {
    const res = await axios.post(ajax_url, form_data);
    if (
      stateRef.current?.billectaAutogiro?.requestState !== "requesting-signage"
    ) {
      return;
    }
    if (res.data?.ReferenceToken) {
      dispatch({
        type: "setBillectaStatus",
        value: "polling-signage",
      });
      dispatch({
        type: "setBillectaReferenceToken",
        value: res.data?.ReferenceToken,
      });
      if (res.data?.AutoStartToken) {
        dispatch({
          type: "setBillectaBankIDToken",
          value: res.data?.AutoStartToken,
        });
      }
      if (res.data?.QR) {
        dispatch({
          type: "setBillectaQRCode",
          value: {
            image: res.data.QR,
          },
        });
      }

      pollSignage(res.data?.ReferenceToken, stateRef, dispatch);

      dispatch({
        type: "setSubmitIsLoading",
        value: false,
      });
    } else {
      dispatch({ type: "setBillectaStatus", value: "failed-signage" });
      handleFailedSignage(dispatch);
      // if error
      if (res.data?.error) {
        dispatch({
          type: "setBillectaErrorMessage",
          value: "Ett fel har inträffat. Var god försök igen.",
        });
      }

      // Loop through all the fields with errors to set error message
      for (var index in res.data.fields) {
        dispatch({
          type: "setFormErrorValue",
          value: {
            name: res.data.fields[index],
            value: getErrorMessage(res.data.fields[index]),
          },
        });
      }

      dispatch({
        type: "setSubmitIsLoading",
        value: false,
      });
    }
  } catch (e) {
    console.error(e);
    dispatch({ type: "setBillectaStatus", value: "failed-signage" });
    handleFailedSignage(dispatch);
    dispatch({
      type: "setBillectaErrorMessage",
      value: "Kunde inte påbörja signering.",
    });
    dispatch({
      type: "setSubmitIsLoading",
      value: false,
    });
  }
};

const pollSignage = async (referenceToken, stateRef, dispatch) => {
  await new Promise((res) => setTimeout(res, 50));

  // TODO - set up timout

  const appdata = stateRef.current;
  const { ajax_url, nonce } = appdata?.backendSetup;

  if (stateRef.current?.billectaAutogiro?.requestState !== "polling-signage") {
    return;
  }

  await new Promise((res) => setTimeout(res, POLLING_INTERVAL_DURATION));

  let poll_bankid_signature_data = new FormData();
  poll_bankid_signature_data.append(
    "action",
    "ktk_donations_poll_bankid_signature"
  );
  poll_bankid_signature_data.append("nonce", nonce);
  poll_bankid_signature_data.append("token", referenceToken);

  try {
    const res = await axios.post(ajax_url, poll_bankid_signature_data);
    if (res.data) {
      const pollStatus = res.data?.status?.toLowerCase();
      if (pollStatus === "started") {
        // Still waiting for signage + updated QR code

        if (res.data?.QR) {
          dispatch({
            type: "setBillectaQRCode",
            value: {
              image: res.data.QR,
            },
          });
        }

        Promise.resolve().then(() =>
          pollSignage(referenceToken, stateRef, dispatch)
        );
      } else if (pollStatus === "complete") {
        dispatch({ type: "setBillectaStatus", value: "success-signage" });
        dispatch({ type: "setBillectaRedirectURL", value: res.data?.url });
      } else if (pollStatus === "error") {
        console.error("failed to sign, show somehow");
        dispatch({ type: "setBillectaStatus", value: "failed-signage" });
        handleFailedSignage(dispatch);
        dispatch({
          type: "setBillectaErrorMessage",
          value: "Signering misslyckades.",
        });
      } else {
        //console.log("Unknown status", pollStatus);
        // Possible values:
        // "OutstandingTransaction",
        // "NoClient",
        // "Started",
        // "UserSign",
        // "UserReq",
        // "Complete",
        // "Error"
      }
    } else {
      console.error("Something went wrong");
      dispatch({ type: "setBillectaStatus", value: "failed-signage" });
      handleFailedSignage(dispatch);
      dispatch({
        type: "setBillectaErrorMessage",
        value: "Signering misslyckades.",
      });
    }
  } catch (err) {
    console.log(err);
    if (err.code === "ERR_NETWORK") {
      Promise.resolve().then(() =>
        pollSignage(referenceToken, stateRef, dispatch)
      );
    } else {
      dispatch({ type: "setBillectaStatus", value: "failed-signage" });
      handleFailedSignage(dispatch);
      dispatch({
        type: "setBillectaErrorMessage",
        value: "Signering misslyckades.",
      });
    }
  }
};

const handleAbortBankAccounts = (dispatch) => {
  if (activeBillectaClient && typeof activeBillectaClient.stop === 'function') {
    activeBillectaClient.stop();
    activeBillectaClient = null;
  }
  
  handleAbortStatus(dispatch);
};

const handleAbortStatus = (dispatch) => {
  dispatch({
    type: "setBillectaStatus",
    value: "idle",
  });
  dispatch({
    type: "setSubmitIsLoading",
    value: false,
  });
  dispatch({
    type: "setBillectaErrorMessage",
    value: null,
  });
};

const handleFailedSignage = (dispatch) => {
  dispatch({
    type: "setBillectaReferenceToken",
    value: null,
  });
  dispatch({
    type: "setBillectaBankIDToken",
    value: null,
  });
  dispatch({
    type: "setBillectaQRCode",
    value: null,
  });
};

const handleAbortSignage = (dispatch) => {
  dispatch({
    type: "setBillectaStatus",
    value: "idle",
  });
  dispatch({
    type: "setSubmitIsLoading",
    value: false,
  });
  dispatch({
    type: "setBillectaErrorMessage",
    value: null,
  });
  dispatch({
    type: "setBillectaReferenceToken",
    value: null,
  });
  dispatch({
    type: "setBillectaBankIDToken",
    value: null,
  });
  dispatch({
    type: "setBillectaQRCode",
    value: null,
  });
};

export {
  initiateGetBankAccounts,
  initiateSignBankId,
  handleAbortBankAccounts,
  handleAbortSignage,
  handleFailedSignage,
};
